import { STUDIO_CAPACITY } from "./capacityConfig.js";

const clamp = (n) => (Number.isFinite(n) ? Math.max(0, n) : 0);

// Legacy helpers preserved for compatibility
function toUtilization(raw = {}) {
  if (typeof raw.utilization === "number") return clamp(raw.utilization);
  if (typeof raw.utilizationPct === "number") return clamp(raw.utilizationPct);
  const current = Number(raw.current ?? raw.used ?? raw.inUse);
  const total = Number(raw.capacity ?? raw.max ?? raw.total);
  if (Number.isFinite(current) && Number.isFinite(total) && total > 0) {
    return clamp(current / total);
  }
  return 0;
}

function labelFor(utilization) {
  const pct = Math.round(clamp(utilization) * 100);
  if (!Number.isFinite(pct)) return "Unknown";
  if (pct >= 110) return `Over capacity (${pct}%)`;
  if (pct >= 95) return `Near capacity (${pct}%)`;
  if (pct >= 75) return `High load (${pct}%)`;
  if (pct >= 50) return `Moderate load (${pct}%)`;
  return `Light load (${pct}%)`;
}

const toDate = (value) => {
  const dt = value ? new Date(value) : null;
  return Number.isFinite(dt?.getTime?.()) ? dt : null;
};

const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

const dateKey = (date) => date.toISOString().slice(0, 10);

function stageMatches(task, stages = []) {
  const key = task.stage || task.status || task.taskType || "";
  return stages.includes(key);
}

function accumulateRange(start, end, cb) {
  const s = toDate(start);
  const e = toDate(end);
  if (!s || !e) return;
  let cursor = new Date(s);
  while (cursor.getTime() <= e.getTime()) {
    cb(cursor);
    cursor = addDays(cursor, 1);
  }
}

function computeDailyLoads(tasks = [], config = STUDIO_CAPACITY) {
  const chart = {};
  const ensure = (d) => {
    if (!chart[d]) chart[d] = { drying: 0, bisque: 0, glaze: 0, firing: 0 };
    return chart[d];
  };

  tasks.forEach((t) => {
    const stage = t.stage || t.status || "";
    if (stage === "drying") {
      accumulateRange(t.startDate || t.start, t.dueDate || t.end, (d) => {
        ensure(dateKey(d)).drying += 1;
      });
    } else if (stage === "bisque") {
      const d = toDate(t.dueDate || t.end || t.start);
      if (d) ensure(dateKey(d)).bisque += 1;
    } else if (stage === "glazing" || stage === "glaze") {
      const d = toDate(t.dueDate || t.end || t.start);
      if (d) ensure(dateKey(d)).glaze += 1;
    } else if (stage === "glazeFiring" || stage === "kiln") {
      const d = toDate(t.dueDate || t.end || t.start);
      if (d) ensure(dateKey(d)).firing += 1;
    }
  });

  return chart;
}

function evaluateDrying(tasks = [], config = STUDIO_CAPACITY) {
  const drying = tasks.filter((t) => stageMatches(t, ["drying"]));
  const loadByDate = {};
  drying.forEach((t) => {
    accumulateRange(t.startDate || t.start, t.dueDate || t.end, (d) => {
      const k = dateKey(d);
      loadByDate[k] = (loadByDate[k] || 0) + 1;
    });
  });

  const warnings = [];
  Object.entries(loadByDate).forEach(([date, count]) => {
    if (count > (config.dryingShelves || STUDIO_CAPACITY.dryingShelves)) {
      warnings.push({
        id: `drying-${date}`,
        severity: "warning",
        message: `Drying capacity exceeded on ${date}`,
        overloadBy: count - (config.dryingShelves || STUDIO_CAPACITY.dryingShelves),
        date,
      });
    }
  });

  return { loadByDate, warnings };
}

function evaluateKiln(tasks = [], config = STUDIO_CAPACITY) {
  const firingStages = ["bisque", "glazeFiring", "glaze", "kiln"];
  const volume = config.kilnVolumeLitres || STUDIO_CAPACITY.kilnVolumeLitres;
  const factor = config.kilnLoadFactor || STUDIO_CAPACITY.kilnLoadFactor || 1;
  const capacityLitres = volume * factor;

  const loadByDate = {};
  tasks
    .filter((t) => stageMatches(t, firingStages))
    .forEach((t) => {
      const d = toDate(t.dueDate || t.end || t.start);
      if (!d) return;
      const k = dateKey(d);
      // crude estimate: 1.5L per item if no volume provided
      const estVolume = Number(t.volumeLitres || t.volume || 1.5);
      loadByDate[k] = (loadByDate[k] || 0) + estVolume;
    });

  const warnings = [];
  Object.entries(loadByDate).forEach(([date, litres]) => {
    if (litres > capacityLitres) {
      warnings.push({
        id: `kiln-${date}`,
        severity: "warning",
        message: `Kiln load exceeds capacity on ${date}`,
        overloadBy: Math.round(litres - capacityLitres),
        date,
      });
    }
  });

  return { loadByDate, warnings, capacityLitres };
}

export function evaluateCapacity(input = {}) {
  // Backward compatibility: if legacy shape, return old calculation
  if (!input.tasks && !input.firings && (input.current !== undefined || input.capacity !== undefined)) {
    const utilizationPct = toUtilization(input);
    let level = "light";
    if (utilizationPct >= 1.1) level = "over";
    else if (utilizationPct >= 0.95) level = "near";
    else if (utilizationPct >= 0.75) level = "high";
    else if (utilizationPct >= 0.5) level = "medium";
    return { level, utilizationPct, label: labelFor(utilizationPct) };
  }

  const tasks = input.tasks || [];
  const config = input.config || STUDIO_CAPACITY;
  const dailyLoadChart = computeDailyLoads(tasks, config);
  const drying = evaluateDrying(tasks, config);
  const kiln = evaluateKiln(tasks, config);

  const timelineConflicts = [...drying.warnings, ...kiln.warnings].map((w) => ({
    date: w.date,
    level: w.overloadBy > 5 ? "error" : "warning",
    label: "Overbooked",
    details: w.message,
  }));

  return {
    dryingLoad: drying.loadByDate,
    dryingWarnings: drying.warnings,
    kilnLoad: kiln.loadByDate,
    kilnWarnings: kiln.warnings,
    dailyLoadChart,
    timelineConflicts,
  };
}

export function capacityLevelLabel(capacity = {}) {
  if (capacity.timelineConflicts?.some?.((c) => c.level === "error")) return "Critical";
  if ((capacity.dryingWarnings?.length || 0) > 0 || (capacity.kilnWarnings?.length || 0) > 0) return "Overloaded";
  if (capacity.level) return labelFor(toUtilization(capacity));
  return "Normal";
}
